import { useState } from "react";
import { Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@packages/hooks/useAnalytics";
import DeleteConfirmPopUp from "@features/tools/components/tool_components/DeleteConfirmPopUp";
import { TextLinear, Text } from "@core/text";
import {
  deleteUbdPlanner,
  getGetUbdPlannersQueryKey,
  useGetUbdPlanners,
  useEditUbdMaarifPlanner,
  getGetUbdPlannerQueryKey,
} from "@packages/api/madlenAPI";
import { PastLessonPlanCard } from "../PastLessonPlanCard";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { CourseData } from "@packages/constant/CourseData";

const PastUbdMaarifPlans = () => {
  const { t } = useTranslation();
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [showDeletePopup, setShowDeletePopup] = useState<number | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  const { trackEvent: track } = trackEvent();

  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { data: lessonPlans, isLoading } = useGetUbdPlanners();

  const { mutateAsync: updateLessonPlanner } = useEditUbdMaarifPlanner({
    mutation: {
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: getGetUbdPlannersQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getGetUbdPlannerQueryKey(data.id!),
        });
      },
      onError: () => {
        addToast({
          title: t("lessonPlanner.titleUpdateError"),
          description: t("lessonPlanner.titleUpdateErrorDescription"),
          variant: "error",
        });
      },
    },
  });
  const handleDelete = async (planId: number) => {
    track(
      {
        category: "ContentDelete",
        action: "ubd.maarif.planner.past.plans.delete",
        label: "UbdMaarifPlannerPastPlans delete click",
        nonInteraction: false,
      },
      {
        location: "/ubd-maarif-planner-past-plans/delete",
        element: "delete-button",
      },
    );
    await deleteUbdPlanner(planId);
    queryClient.invalidateQueries({
      queryKey: getGetUbdPlannersQueryKey(),
    });
  };

  const handleViewDetails = (planId: number) => {
    track(
      {
        category: "ContentViewDetail",
        action: "ubd.maarif.planner.past.plans.view.detail",
        label: "UbdMaarifPlannerPastPlans view details click",
        nonInteraction: false,
      },
      {
        location: "/ubd-maarif-planner-past-plans/view-detail",
        element: "view-detail-button",
      },
    );
    navigate(`/lesson-planning-tools/ubd-maarif-plan/${planId}`);
  };

  const handleClickGenerate = () => {
    track(
      {
        category: "ContentGeneration",
        action: "ubd.maarif.planner.past.plans.generate",
        label: "UbdMaarifPlannerPastPlans generation click",
        nonInteraction: false,
      },
      {
        location: "/ubd-maarif-planner-past-plans/generate",
        element: "generate-button",
      },
    );
    navigate("/lesson-planning-tools/ubd-maarif-plan/new");
  };

  const handleClickFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    track(
      {
        category: "ContentFilter",
        action: "ubd.maarif.planner.past.plans.filter",
        label: "UbdMaarifPlannerPastPlans filter click",
        nonInteraction: false,
      },
      {
        location: "/ubd-maarif-planner-past-plans/filter",
        element: "filter-button",
      },
    );
    setSelectedCourse(e.target.value);
  };

  const courseTypes = Object.keys(CourseData);

  // Ensure lessonPlans is always an array before filtering
  const plansArray = Array.isArray(lessonPlans) ? lessonPlans : [];

  const filteredPlans = plansArray.filter((plan) => {
    const matchesCourse = selectedCourse
      ? plan.courseType === selectedCourse
      : true;

    return matchesCourse;
  });

  const handleUpdateTitle = async (planId: number, newTitle: string) => {
    await updateLessonPlanner({
      id: planId,
      data: {
        name: newTitle,
      },
    });
  };

  const showEmptyState =
    !isLoading && (!filteredPlans || filteredPlans.length === 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
      <div className="container max-w-7xl mx-auto px-4 py-6">
        <div className="mb-8 text-center space-y-4">
          <div className="max-w-4xl mx-auto">
            <TextLinear
              variant={"heroTitle"}
              className="leading-tight"
              from="from-purple-600 dark:from-purple-400"
              to="to-indigo-600 dark:to-indigo-400"
            >
              {t("ubdMaarifPlan.title")}
            </TextLinear>
          </div>
          <Text
            variant="p"
            status="secondary"
            size="base"
            className="max-w-2xl mx-auto"
          >
            {t("ubdMaarifPlan.description")}
          </Text>
          <div className="pt-2">
            <button
              onClick={handleClickGenerate}
              className="btn bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 text-white px-8 py-3 text-base rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-1 font-medium"
            >
              {t("ubdMaarifPlan.createNew")}
            </button>
          </div>
        </div>

        {!showEmptyState && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {filteredPlans?.map((plan) => (
              <PastLessonPlanCard
                key={plan.id}
                plan={plan}
                onViewDetails={handleViewDetails}
                onDelete={() => setShowDeletePopup(plan.id)}
                colorTheme="ubd"
                onUpdateTitle={handleUpdateTitle}
              />
            ))}
          </div>
        )}

        {showEmptyState && (
          <div className="flex flex-col items-center justify-center min-h-[500px] space-y-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-sm p-16 border border-gray-100 dark:border-gray-700">
            <div className="p-8 rounded-full bg-purple-50 dark:bg-purple-900/30 ring-8 ring-purple-50/50 dark:ring-purple-900/20">
              <Package
                size={64}
                className="text-purple-600 dark:text-purple-400"
              />
            </div>
            <div className="text-center space-y-4 max-w-lg">
              <h3 className="text-3xl font-semibold text-gray-900 dark:text-white">
                {selectedCourse
                  ? t("ubdMaarifPlan.noResults.title")
                  : t("ubdMaarifPlan.empty.title")}
              </h3>
              <Text variant="p" status="subtle" size="lg">
                {selectedCourse
                  ? t("ubdMaarifPlan.noResults.description")
                  : t("ubdMaarifPlan.empty.description")}
              </Text>
            </div>
            <button
              onClick={handleClickGenerate}
              className="btn bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 text-white px-10 py-5 text-lg rounded-2xl shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-1 font-medium"
            >
              {t("ubdMaarifPlan.empty.createButton")}
            </button>
          </div>
        )}

        {showDeletePopup && (
          <DeleteConfirmPopUp
            title={t("dailyPlanner.confirmDelete", {
              title: filteredPlans?.find((plan) => plan.id === showDeletePopup)
                ?.name,
            })}
            showDeletePopup={showDeletePopup}
            setShowDeletePopup={setShowDeletePopup}
            handleDelete={() => handleDelete(showDeletePopup)}
          />
        )}
      </div>
    </div>
  );
};

export default PastUbdMaarifPlans;
